import type {
  IAssetTemplateModalData,
  IAssetTemplateDetail,
  IAssetTemplateStatus,
  IDisableReasonKey,
  ITemplateUsageDetail,
  IUsedTemplateSuffixes,
} from '../types';
import type { AssetTemplate, AssetTemplates } from '~/modules/shopify';
import type { IBaseCampaignGroup } from '~/modules/campaign-group';
import type { IBaseCampaign } from '~/modules/campaign/types';

import {
  getStateDisable,
  checkAssignmentStatus,
  filterTemplateHasAssigned,
  checkTemplateNoSpecifiedPageAvailable,
} from '../utils';
import { IAssetTemplateModalType, PICKED_TEMPLATE_FOR_VARIANT_A } from '../types';
import { usePriceCampaignTemplateUsage } from './usePriceCampaignTemplateUsage';
import { useUrlCampaignTemplateUsage } from './useUrlCampaignTemplateUsage';
import { useCampaignGroupStore } from '~/modules/campaign-group/stores';
import { ICampaignType, IVariantType } from '~/modules/campaign/types';
import { useAssetTemplateContext } from '~/modules/asset-template';
import { toTemplateAssetKeys, toTemplateSuffixes } from '~/utils';
import { findVariantByOrigin } from '~/modules/campaign/utils';
import { useCampaignStore } from '~/modules/campaign/stores';
import { CAMPAIGN_STATUS } from '~/constants/campaign';
import { useShopifyAsset } from '~/modules/shopify';
import { useQueryThemePages } from '~/modules/apps';
import { useCallback, useMemo } from 'react';

export interface IAssetTemplateUnavailableProps {
  campaigns: IBaseCampaign[];
  campaign?: IBaseCampaign;
  campaignGroup?: IBaseCampaignGroup;
}

export interface IAssetTemplateUnavailableResult {
  usedTemplateSuffixes: IUsedTemplateSuffixes;
  getTemplateStateDisable: (props: {
    template: AssetTemplate;
    modalType?: IAssetTemplateModalType;
    modalData?: IAssetTemplateModalData;
    templateDetail: IAssetTemplateDetail;
    templateStatus: IAssetTemplateStatus;
  }) => IDisableReasonKey | undefined;
  filterTemplateUnavailable: (props: {
    templates: AssetTemplates;
    isSelectedDisplayAll: boolean;
  }) => AssetTemplate[];
  handleRefetchPriceProducts: () => Promise<unknown>;
  isFetchingPriceProducts: boolean;
  handleRefetchUrlDestinations: () => Promise<unknown>;
  isFetchingUrlDestinations: boolean;
}
export const useInitializeAssetTemplateUnavailable = ({
  campaigns,
  campaign,
  campaignGroup,
}: IAssetTemplateUnavailableProps): IAssetTemplateUnavailableResult => {
  const dataCampaignGroups = useCampaignGroupStore((state) => state.campaignGroups);
  const dataCampaigns = useCampaignStore((state) => state.campaigns);

  const { modalAssetTemplate } = useAssetTemplateContext();
  const { modalType, modalData } = modalAssetTemplate;

  // Both usage hooks resolve the template a page renders against the theme's own templates, so the
  // assets are read once here and each pass handed down -- `useShopifyAsset` stringifies the whole
  // asset store on every render, which is not worth paying twice. Two passes rather than deriving
  // the keys from the map: the map is keyed by bare suffix, so `product.gp-a` and `page.gp-a` share
  // an entry, and its values alone would report one of the two as missing from the theme.
  const { assets } = useShopifyAsset();
  const templateSuffixes = useMemo(() => toTemplateSuffixes(assets), [assets]);
  const templateAssetKeys = useMemo(
    () => (assets ? toTemplateAssetKeys(assets) : undefined),
    [assets],
  );
  // Only the homepage needs these: it has no record to read a template off, so whichever theme page
  // is default answers for it. Keyed off the same assets as the template listing's own call, so the
  // pages are already cached rather than fetched twice.
  const { themePages } = useQueryThemePages({ assets });

  const { priceCampaignTemplateUsage, handleRefetchPriceProducts, isFetchingPriceProducts } =
    usePriceCampaignTemplateUsage({ excludedCampaignId: campaign?.id, templateSuffixes });

  const { urlCampaignTemplateUsage, handleRefetchUrlDestinations, isFetchingUrlDestinations } =
    useUrlCampaignTemplateUsage({
      excludedCampaignId: campaign?.id,
      templateAssetKeys,
      themePages,
    });

  const isModalPickVariantB = modalType === IAssetTemplateModalType.VARIANT_B;
  const isModalPickVariantA = !!modalType && PICKED_TEMPLATE_FOR_VARIANT_A.includes(modalType);

  const singleCampaignTemplateUsage = useMemo(() => {
    const singleCampaignTemplates =
      dataCampaigns?.filter(
        (item) => item.status !== CAMPAIGN_STATUS.STOPPED && item.id !== campaign?.id,
      ) || [];
    const usage: Record<string, ITemplateUsageDetail[]> = {};
    singleCampaignTemplates.forEach((campaign) => {
      const templateSuffixes =
        campaign.variants
          ?.map((variant) => variant?.templateSuffix)
          .filter((suffix): suffix is string => !!suffix) || [];
      templateSuffixes.forEach((suffix) => {
        if (!usage[suffix]) {
          usage[suffix] = [];
        }
        usage[suffix].push({
          campaignId: campaign.id,
          campaignStatus: campaign.status ?? undefined,
          campaignName: campaign.name,
          isOtherSingerCampaign: true,
        });
      });
    });
    return usage;
  }, [campaign?.id, dataCampaigns]);

  const otherGroupCampaignTemplateUsage = useMemo(() => {
    const otherGroups = dataCampaignGroups
      .filter((item) => item.status !== CAMPAIGN_STATUS.STOPPED)
      .filter((item) => item.id !== campaignGroup?.id)
      .filter((item) => item.type === ICampaignType.TEMPLATE_BUNDLE);

    const usage: Record<string, ITemplateUsageDetail[]> = {};
    otherGroups.forEach((group) => {
      const groupCampaigns = (group.campaigns || []).filter((item) => !item.deletedAt);
      groupCampaigns.forEach((campaign) => {
        const templateSuffixes =
          campaign.variants
            ?.map((variant) => variant?.templateSuffix)
            .filter((suffix): suffix is string => !!suffix) || [];
        templateSuffixes.forEach((suffix) => {
          if (!usage[suffix]) {
            usage[suffix] = [];
          }
          usage[suffix].push({
            campaignId: group.id || '',
            campaignStatus: group.status ?? undefined,
            campaignName: group.title,
            isOtherGroupCampaign: true,
          });
        });
      });
    });
    return usage;
  }, [dataCampaignGroups, campaignGroup?.id]);

  const currentCampaignGroupTemplateUsage = useMemo(() => {
    const usage: Record<string, ITemplateUsageDetail[]> = {};
    if (isModalPickVariantA) {
      const currentTemplateSuffixB =
        campaigns
          ?.flatMap((campaign) => findVariantByOrigin(campaign, false)?.templateSuffix)
          .filter((suffix): suffix is string => !!suffix) || [];

      currentTemplateSuffixB.forEach((suffix) => {
        if (!usage[suffix]) {
          usage[suffix] = [];
        }
        usage[suffix].push({
          campaignId: campaignGroup?.id || '',
          currentCampaignGroupType: IVariantType.VARIANT_B,
        });
      });

      const currentTemplateSuffix = modalData?.variant.templateSuffix;
      if (currentTemplateSuffix) {
        if (!usage[currentTemplateSuffix]) {
          usage[currentTemplateSuffix] = [];
        }
        usage[currentTemplateSuffix].push({
          campaignId: campaignGroup?.id || '',
          currentCampaignGroupType: IVariantType.VARIANT_A,
        });
      }
    }

    if (isModalPickVariantB) {
      const currentTemplateSuffixOfA =
        campaigns
          ?.flatMap((campaign) => findVariantByOrigin(campaign, true)?.templateSuffix)
          .filter((suffix): suffix is string => !!suffix) || [];

      const currentTemplateSuffixOfB =
        campaigns
          ?.flatMap((campaign) => findVariantByOrigin(campaign, false)?.templateSuffix)
          .filter((suffix): suffix is string => !!suffix) || [];

      currentTemplateSuffixOfA.forEach((suffix) => {
        if (!usage[suffix]) {
          usage[suffix] = [];
        }
        usage[suffix].push({
          campaignId: campaignGroup?.id || '',
          currentCampaignGroupType: IVariantType.VARIANT_A,
        });
      });
      currentTemplateSuffixOfB.forEach((suffix) => {
        if (!usage[suffix]) {
          usage[suffix] = [];
        }
        usage[suffix].push({
          campaignId: campaignGroup?.id || '',
          currentCampaignGroupType: IVariantType.VARIANT_B,
        });
      });
    }
    return usage;
  }, [
    campaignGroup?.id,
    campaigns,
    isModalPickVariantA,
    isModalPickVariantB,
    modalData?.variant.templateSuffix,
  ]);

  const currentCampaignTemplateUsage = useMemo(() => {
    const usage: Record<string, ITemplateUsageDetail[]> = {};
    if (isModalPickVariantA) {
      const currentTemplateSuffixB =
        campaigns
          ?.flatMap((campaign) => findVariantByOrigin(campaign, false)?.templateSuffix)
          .filter((suffix): suffix is string => !!suffix) || [];

      currentTemplateSuffixB.forEach((suffix) => {
        if (!usage[suffix]) {
          usage[suffix] = [];
        }
        usage[suffix].push({
          campaignId: campaign?.id || '',
          currentCampaignType: IVariantType.VARIANT_B,
        });
      });

      const currentTemplateSuffix = modalData?.variant.templateSuffix;
      if (currentTemplateSuffix) {
        if (!usage[currentTemplateSuffix]) {
          usage[currentTemplateSuffix] = [];
        }
        usage[currentTemplateSuffix].push({
          campaignId: campaign?.id || '',
          currentCampaignType: IVariantType.VARIANT_A,
        });
      }
    }

    if (isModalPickVariantB) {
      const currentTemplateSuffixOfA =
        campaigns
          ?.flatMap((campaign) => findVariantByOrigin(campaign, true)?.templateSuffix)
          .filter((suffix): suffix is string => !!suffix) || [];

      const currentTemplateSuffixOfB =
        campaigns
          ?.flatMap((campaign) => findVariantByOrigin(campaign, false)?.templateSuffix)
          .filter((suffix): suffix is string => !!suffix) || [];

      currentTemplateSuffixOfA.forEach((suffix) => {
        if (!usage[suffix]) {
          usage[suffix] = [];
        }
        usage[suffix].push({
          campaignId: campaign?.id || '',
          currentCampaignType: IVariantType.VARIANT_A,
        });
      });
      currentTemplateSuffixOfB.forEach((suffix) => {
        if (!usage[suffix]) {
          usage[suffix] = [];
        }
        usage[suffix].push({
          campaignId: campaign?.id || '',
          currentCampaignType: IVariantType.VARIANT_B,
        });
      });
    }
    return usage;
  }, [
    campaign?.id,
    campaigns,
    isModalPickVariantA,
    isModalPickVariantB,
    modalData?.variant.templateSuffix,
  ]);

  const usedTemplateSuffixes = useMemo(() => {
    const combinedUsage: IUsedTemplateSuffixes = {};

    const mergeUsage = (suffix: string, detail: ITemplateUsageDetail) => {
      if (!combinedUsage[suffix]) {
        combinedUsage[suffix] = [];
      }
      combinedUsage[suffix].push(detail);
    };

    Object.entries(singleCampaignTemplateUsage).forEach(([suffix, details]) => {
      details.forEach((detail) => mergeUsage(suffix, detail));
    });

    Object.entries(otherGroupCampaignTemplateUsage).forEach(([suffix, details]) => {
      details.forEach((detail) => mergeUsage(suffix, detail));
    });

    Object.entries(currentCampaignGroupTemplateUsage).forEach(([suffix, details]) => {
      details.forEach((detail) => mergeUsage(suffix, detail));
    });

    Object.entries(currentCampaignTemplateUsage).forEach(([suffix, details]) => {
      details.forEach((detail) => mergeUsage(suffix, detail));
    });

    Object.entries(priceCampaignTemplateUsage).forEach(([suffix, details]) => {
      details.forEach((detail) => mergeUsage(suffix, detail));
    });

    Object.entries(urlCampaignTemplateUsage).forEach(([suffix, details]) => {
      details.forEach((detail) => mergeUsage(suffix, detail));
    });

    return combinedUsage;
  }, [
    singleCampaignTemplateUsage,
    otherGroupCampaignTemplateUsage,
    currentCampaignGroupTemplateUsage,
    currentCampaignTemplateUsage,
    priceCampaignTemplateUsage,
    urlCampaignTemplateUsage,
    // shopifyOriginTemplateUsage,
  ]);

  const filterTemplateUnavailable = useCallback(
    (props: { templates: AssetTemplates; isSelectedDisplayAll: boolean }) => {
      const { templates, isSelectedDisplayAll } = props;
      if (!templates) return [];

      let dataTemplates = templates;

      // TODO: Check logic for current template suffix selected
      const currentTemplateSuffix = modalData?.variant.templateSuffix;
      if (currentTemplateSuffix) {
        dataTemplates = dataTemplates.filter((asset) => asset.key !== currentTemplateSuffix);
      }

      if (isSelectedDisplayAll) {
        return dataTemplates.map((asset) => {
          const isUsed = usedTemplateSuffixes[asset.key];
          const hasPagesAssigned = filterTemplateHasAssigned(asset, modalType);
          if (isUsed || !hasPagesAssigned) {
            return {
              ...asset,
              disabled: true,
            };
          }

          return asset;
        });
      }

      const filteredDataTemplates = dataTemplates.filter((asset) => {
        const isUsed = usedTemplateSuffixes[asset.key];

        const isDisabledAssignment = checkAssignmentStatus({
          template: asset,
          modalData,
          modalType,
        });
        if (isUsed || !!isDisabledAssignment) return false;

        const isDisabledWhenNoSpecifiedPage = checkTemplateNoSpecifiedPageAvailable({
          asset,
          campaigns,
        });
        if (isDisabledWhenNoSpecifiedPage) return false;

        return true;
      });
      return filteredDataTemplates;
    },
    [campaigns, modalData, modalType, usedTemplateSuffixes],
  );

  const getTemplateStateDisable = useCallback(
    (props: {
      template: AssetTemplate;
      modalType?: IAssetTemplateModalType;
      modalData?: IAssetTemplateModalData;
      templateDetail: IAssetTemplateDetail;
      templateStatus: IAssetTemplateStatus;
    }) => {
      return getStateDisable({
        ...props,
        modalType,
        modalData,
        usedTemplateSuffixes,
        currentTemplateSuffix: modalData?.variant.templateSuffix,
      });
    },
    [modalType, modalData, usedTemplateSuffixes],
  );

  return {
    usedTemplateSuffixes,
    getTemplateStateDisable,
    filterTemplateUnavailable,
    handleRefetchPriceProducts,
    isFetchingPriceProducts,
    handleRefetchUrlDestinations,
    isFetchingUrlDestinations,
  };
};
